/**
 * Marca do produto.
 *
 * O símbolo funde duas leituras: a moldura é a assinatura de um Data Matrix —
 * a borda sólida em "L" de dois lados e os módulos destacados nos outros dois —
 * e dentro dela uma válvula vista de frente, com a passagem vazada, as orelhas
 * de fixação e a haste. É o produto em um desenho: a plaqueta que se lê e o
 * ativo que ela identifica.
 *
 * Desenha em `currentColor` de propósito. O arquivo
 * `src/assets/logo_passaporte_valvula.svg` é o mesmo desenho com a cor travada
 * em `#3D9BFF`, para uso **fora** da aplicação (favicon, impressão). Aqui a cor
 * precisa ser herdada: a marca aparece em azul no login e em branco sobre o
 * grafite do topo do painel, e um atributo `color` fixo venceria a herança.
 *
 * Abaixo de ~24 px os módulos do Data Matrix empastam. Nos usos atuais ela
 * nunca aparece tão pequena; se um dia precisar, o caminho é uma variante sem
 * os módulos, não encolher esta.
 */

import { useId } from "react";

interface Props {
  /** Lado do quadrado, em pixels. */
  tamanho?: number;
  /**
   * Texto alternativo. Sem ele a marca é decorativa — que é o caso quando o
   * nome do produto já aparece escrito ao lado dela.
   */
  titulo?: string;
}

export default function Logo({ tamanho = 28, titulo }: Props) {
  // O recorte dos furos precisa de um id, e a marca pode aparecer duas vezes na
  // mesma página. Os dois-pontos que o `useId` gera atrapalham em `url(#…)`.
  const id = `marca-${useId().replace(/:/g, "")}`;

  return (
    <svg
      viewBox="20 32 448 448"
      width={tamanho}
      height={tamanho}
      fill="none"
      role={titulo ? "img" : "presentation"}
      aria-label={titulo}
      aria-hidden={titulo ? undefined : true}
      focusable="false"
    >
      {/*
        Os furos das orelhas são recortados do desenho inteiro, não só das
        orelhas. O traço do corpo da válvula passa por trás delas — na altura do
        furo ocupa de 147 a 181 em x, cobrindo quase toda a orelha —, então
        vazar apenas a orelha revelaria o próprio traço, não o fundo.
      */}
      <mask id={id}>
        <rect x="20" y="32" width="448" height="448" fill="#fff" />
        <circle cx="154" cy="272" r="9" fill="#000" />
        <circle cx="358" cy="272" r="9" fill="#000" />
      </mask>

      <g mask={`url(#${id})`}>
        <path
          d="M72 96V416H416"
          stroke="currentColor"
          strokeWidth="40"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <rect x="128" y="76" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="192" y="76" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="256" y="76" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="320" y="76" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="384" y="76" width="32" height="40" rx="4" fill="currentColor" />

        <rect x="396" y="132" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="396" y="196" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="396" y="260" width="40" height="40" rx="4" fill="currentColor" />
        <rect x="396" y="324" width="40" height="40" rx="4" fill="currentColor" />

        <rect x="210" y="126" width="92" height="28" rx="4" fill="currentColor" />
        <rect x="244" y="150" width="24" height="34" rx="4" fill="currentColor" />

        <circle cx="256" cy="272" r="92" stroke="currentColor" strokeWidth="34" />

        <rect x="128" y="238" width="54" height="68" rx="10" fill="currentColor" />
        <rect x="330" y="238" width="54" height="68" rx="10" fill="currentColor" />
      </g>
    </svg>
  );
}
